import { Link } from '@tanstack/react-router';
import { WarningIcon } from '@phosphor-icons/react';
import { T } from 'gt-react';
import type { AgentConfigWarning } from '@erp/rpc';
import {
  AlertContainer,
  AlertDescription,
  AlertTitle,
} from 'src/components/ui/alert';
import { cn } from 'src/lib/utils';
import paths from 'src/paths';

/**
 * Configuration problems the backend predicts will fail this agent's runs.
 * Advisory only — saving stays allowed, since the user may be mid-setup.
 */
export function AgentConfigWarningsNotice({
  warnings,
  agentDefinitionId,
  className,
}: {
  warnings: AgentConfigWarning[] | undefined;
  agentDefinitionId: string;
  className?: string;
}) {
  if (
    !warnings?.some(
      (warning) => warning.code === 'remote_desktop_no_login_source',
    )
  ) {
    return null;
  }

  return (
    <AlertContainer
      className={cn(
        'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200',
        className,
      )}
    >
      <WarningIcon />
      <AlertTitle>
        <T context="agent warning title when an agent uses remote desktop tools but has no way to log in">
          Remote desktop login is not configured
        </T>
      </AlertTitle>
      <AlertDescription className="text-amber-800 dark:text-amber-300">
        <p>
          <T context="agent warning when an agent uses remote desktop tools but has no way to log in">
            This agent uses remote desktop tools but has no way to log in, so
            its runs will fail at login. Configure remote desktop credentials on
            this agent, or add a setup workflow with a Login to Remote Desktop
            step.
          </T>
        </p>
        <div className="flex gap-4">
          <Link
            to={paths.authenticated.backgroundAgents.definitionTools}
            params={{ agentDefinitionId }}
            className="font-medium underline"
          >
            <T context="link to the agent's Tools tab, where remote desktop credentials are configured">
              Configure credentials
            </T>
          </Link>
          <Link
            to={paths.authenticated.backgroundAgents.definitionTaskManagement}
            params={{ agentDefinitionId }}
            className="font-medium underline"
          >
            <T context="link to the agent's Task Management tab, where setup workflows are configured">
              Add a setup workflow
            </T>
          </Link>
        </div>
      </AlertDescription>
    </AlertContainer>
  );
}
